/**
 * `/debug-data` — dev-only debug page.
 *
 * Renders raw JSON from `useWorkspace`, `useUsage`, and
 * `useAudits({limit:5})` so the Stage 4 data layer can be eyeballed
 * without a full page harness. Not linked anywhere in nav.
 *
 * `import.meta.env.DEV` is inlined by Vite at build time — in
 * production the route renders a "not found" placeholder instead of
 * the real content so the bundle stays symbol-dead.
 *
 * Route lives inside `_app` so `AuthGate` + shell chrome wrap it
 * for free. URL: `/debug-data`.
 */
import { useAudits } from '@/lib/queries/audits';
import { useUsage } from '@/lib/queries/usage';
import { useWorkspace } from '@/lib/queries/workspace';
import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

export const Route = createFileRoute('/_app/debug-data')({
  component: import.meta.env.DEV ? DataDebug : NotFound,
});

function NotFound(): ReactElement {
  return (
    <div className="p-8 text-sm text-[var(--color-mute)]">
      <p>This page is only available in development builds.</p>
    </div>
  );
}

function Section({
  title,
  data,
  error,
}: { title: string; data: unknown; error: unknown }): ReactElement {
  return (
    <section className="rounded-md border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <header className="mb-2 flex items-center justify-between">
        <h2 className="font-medium text-sm">{title}</h2>
        {error ? (
          <span className="text-[var(--color-danger)] text-xs">
            {String((error as Error)?.message ?? error)}
          </span>
        ) : null}
      </header>
      <pre className="max-h-96 overflow-auto rounded bg-[var(--color-paper-alt)] p-3 font-mono text-xs">
        {data === undefined ? '(loading)' : JSON.stringify(data, null, 2)}
      </pre>
    </section>
  );
}

function DataDebug(): ReactElement {
  const workspace = useWorkspace();
  const usage = useUsage();
  const audits = useAudits({ limit: 5 });

  return (
    <div className="flex flex-col gap-4 p-6">
      <header>
        <h1 className="font-semibold text-lg">Data layer debug</h1>
        <p className="text-[var(--color-mute)] text-sm">
          Dev-only. Renders raw JSON from the Stage 4 hooks so the wiring can be eyeballed
          end-to-end.
        </p>
      </header>
      <Section
        title="useWorkspace() — GET /v1/workspace"
        data={workspace.data}
        error={workspace.error}
      />
      <Section title="useUsage() — GET /v1/usage" data={usage.data} error={usage.error} />
      <Section
        title="useAudits({ limit: 5 }) — GET /v1/audits"
        data={audits.data}
        error={audits.error}
      />
    </div>
  );
}
